import { ApprovalMode, ToolLoadingMode } from '../../../../api/agents-client/agent-client.type';

/**
 * A selectable option shown in one of the General section's dropdowns.
 */
interface SettingOption<TMode extends string> {
  mode: TMode;
  label: string;
  description: string;
  recommended?: boolean;
}

/**
 * A selectable tool-loading strategy shown in the General section's dropdown.
 */
type ToolLoadingOption = SettingOption<ToolLoadingMode>;

/**
 * The tool-loading strategies offered in the General section, in display order.
 */
const TOOL_LOADING_OPTIONS: ToolLoadingOption[] = [
  {
    mode: ToolLoadingMode.OnDemand,
    label: 'Search & load on demand',
    description: 'Flora finds the right tool when a task needs it. Scales to hundreds of tools.',
    recommended: true,
  },
  {
    mode: ToolLoadingMode.All,
    label: 'Load all tools upfront',
    description: 'Every enabled tool loads at chat start. Fastest first response, more context used.',
  },
];

/**
 * The default approval modes offered in the General section, in display order.
 */
const APPROVAL_MODE_OPTIONS: SettingOption<ApprovalMode>[] = [
  {
    mode: ApprovalMode.Ask,
    label: 'Ask',
    description: 'Your approval gates apply: Flora waits for your OK on any action that requires approval.',
  },
  {
    mode: ApprovalMode.Auto,
    label: 'Auto',
    description: 'Flora handles permission decisions: safe actions run, and risky ones are blocked.',
  },
];

/**
 * The material-icon ligature + colors used to render a capability or section
 * badge. `color` styles the glyph; `badgeBg` styles the badge background.
 */
interface CustomizeVisual {
  icon: string;
  color: string;
  badgeBg: string;
}

const DEFAULT_VISUAL: CustomizeVisual = {
  icon: 'extension',
  color: 'var(--secondary-base)',
  badgeBg: 'var(--secondary-lightest)',
};

/**
 * Badge visuals keyed by top-level section id (bucket or standalone
 * capability). Falls back to a neutral badge for unknown sections so newly
 * shipped sections still render.
 */
const SECTION_VISUALS: Record<string, CustomizeVisual> = {
  google: { icon: 'cloud', color: 'var(--blue-darkest)', badgeBg: 'var(--blue-lightest)' },
  office: { icon: 'description', color: 'var(--blue-darkest)', badgeBg: 'var(--sky-lightest)' },
  slash: { icon: 'terminal', color: 'var(--secondary-base)', badgeBg: 'var(--secondary-lightest)' },
  automations: { icon: 'bolt', color: 'var(--purple-base)', badgeBg: 'var(--purple-lightest)' },
  targets: { icon: 'track_changes', color: 'var(--yellow-darkest)', badgeBg: 'var(--yellow-lightest)' },
  flora_pages: { icon: 'article', color: 'var(--primary-darkest)', badgeBg: 'var(--primary-lightest)' },
  documents: { icon: 'upload_file', color: 'var(--blue-darkest)', badgeBg: 'var(--blue-lightest)' },
};

/**
 * Badge visuals keyed by capability id (bucket children). Falls back to a
 * neutral badge for unknown capabilities.
 */
const CAPABILITY_VISUALS: Record<string, CustomizeVisual> = {
  docs: { icon: 'description', color: 'var(--secondary-base)', badgeBg: 'var(--secondary-lightest)' },
  slides: { icon: 'slideshow', color: 'var(--yellow-darkest)', badgeBg: 'var(--yellow-lightest)' },
  sheets: { icon: 'grid_on', color: 'var(--primary-darkest)', badgeBg: 'var(--primary-lightest)' },
  drive: { icon: 'folder', color: 'var(--blue-darkest)', badgeBg: 'var(--blue-lightest)' },
  export: { icon: 'picture_as_pdf', color: 'var(--red-darkest)', badgeBg: 'var(--red-lightest)' },
  excel: { icon: 'grid_on', color: 'var(--primary-darkest)', badgeBg: 'var(--primary-lightest)' },
  word: { icon: 'description', color: 'var(--blue-darkest)', badgeBg: 'var(--blue-lightest)' },
  general: { icon: 'tune', color: 'var(--secondary-base)', badgeBg: 'var(--secondary-lightest)' },
};

/**
 * Resolves the badge visual for a top-level section id.
 *
 * @param {string} id - The section id (e.g. "google", "slash")
 * @returns {CustomizeVisual} The icon + colors, or a neutral fallback
 */
function getSectionVisual(id: string): CustomizeVisual {
  return SECTION_VISUALS[id] ?? DEFAULT_VISUAL;
}

/**
 * Resolves the badge visual for a capability id.
 *
 * @param {string} id - The capability id (e.g. "docs", "sheets")
 * @returns {CustomizeVisual} The icon + colors, or a neutral fallback
 */
function getCapabilityVisual(id: string): CustomizeVisual {
  return CAPABILITY_VISUALS[id] ?? DEFAULT_VISUAL;
}

/**
 * The read/write group badge glyph + color. Read groups use an eye; write
 * groups use a pencil, matching the design's kind icons.
 *
 * @param {'read' | 'write'} kind - The group kind
 * @returns {{ icon: string; color: string; badgeBg: string }} The group badge visual
 */
function getGroupVisual(kind: 'read' | 'write'): CustomizeVisual {
  return kind === 'read'
    ? { icon: 'visibility', color: 'var(--secondary-base)', badgeBg: 'var(--secondary-lightest)' }
    : { icon: 'edit', color: 'var(--yellow-darkest)', badgeBg: 'var(--yellow-lightest)' };
}

export { APPROVAL_MODE_OPTIONS, getCapabilityVisual, getGroupVisual, getSectionVisual, TOOL_LOADING_OPTIONS };
export type { CustomizeVisual, SettingOption, ToolLoadingOption };
